import React, { useState, useEffect, useRef } from "react";

import { Edit } from "@mui/icons-material";
import { Box, Button, CircularProgress, Divider, IconButton, Typography } from "@mui/material"

import { CreateArtefacts } from "./CreateArtefacts";

import { DrawerControl } from "../../../shared/components";
import { formatDate } from "../../../shared/utils/formatDate";

import { useFetchArtefacts } from "../hooks/useFetchArtefacts";
import { useCapabilities } from "../../../hooks/useCapabilities";
import { Artefact } from "../types";

export const ProjectArtefact = ({ projectId, stageGateId, isStageGateCompleted }: { projectId: string | undefined, stageGateId: number | null, isStageGateCompleted: boolean }) => {

    const { isAllowedAddChecklist, isAllowedEditChecklist } = useCapabilities();

    const [page, setPage] = useState<number>(1);
    const [limit, setLimit] = useState<number>(25);
    const [accumulatedData, setAccumulatedData] = useState<Artefact[]>([]);

    const { isPending, artefactsData, artefactsPagination, refetch: refetchArtefacts } = useFetchArtefacts({ projectId, stageGateId, page, limit, searchKey: null, assignedToMe: false });

    useEffect(() => {
        setPage(1);
        setAccumulatedData([]);
    }, [projectId, stageGateId]);

    useEffect(() => {
        if (artefactsData && Array.isArray(artefactsData)) {
            setAccumulatedData(prev => {
                if (page === 1) {
                    if (prev.length === 0 && artefactsData.length === 0) return prev;
                    if (prev === artefactsData) return prev;
                    return artefactsData;
                }
                const newItems = artefactsData.filter((newItem) => !prev.some((oldItem) => oldItem.id === newItem.id));
                if (newItems.length === 0) return prev;
                return [...prev, ...newItems];
            });
        }
    }, [artefactsData, page]);

    const scrollTimeout = useRef<number | null>(null);

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const target = e.currentTarget;

        if(scrollTimeout.current) clearTimeout(scrollTimeout.current);

        scrollTimeout.current = window.setTimeout(() => {
            if (!target) return;
            
            const threshold = 50;
            const atBottom = target.scrollHeight - target.scrollTop - target.clientHeight < threshold;
            if (atBottom && artefactsPagination) {
                if (page * artefactsPagination.limit < artefactsPagination.total && !isPending) {
                    setPage(prevPage => prevPage + 1);
                }
            }
        }, 100);
    };

    const [formStatus, setFormStatus] = useState(false);

    const [editStatus, setEditStatus] = useState<{ status: boolean; id: string | null }>({
        status: false,
        id: null
    });

    if (isPending && page === 1) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <CircularProgress />
            </Box>
        )
    };

    return (
        <Box display={'flex'} flexDirection={'column'} alignItems={'end'} overflow={'auto'} flex={1} pl={2}>

            <Box flex={1} display={'flex'} flexDirection={'column'} gap={2} width={'100%'} overflow={'auto'} mb={2} pr={2} onScroll={handleScroll}>
                {
                    isAllowedAddChecklist && !isStageGateCompleted && (
                        <Button onClick={() => setFormStatus(true)} variant="outlined">Create Artefact</Button>
                    )
                }
                {
                    (!isPending && accumulatedData.length === 0) ?
                        <Box
                            height="100%"
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            width="100%"
                            gap={2}
                        >
                            <Typography variant="subtitle1">No artefacts found.</Typography>
                        </Box>
                        :
                        accumulatedData.map((artefact) => (
                            <Box
                                key={artefact.id}
                                border="1px solid"
                                borderColor="divider"
                                borderRadius={1}
                                p={2}
                            >
                                <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'} flexWrap={'wrap'} gap={1}>
                                    <Typography variant="subtitle2" sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: '80%' }}>{artefact.title}</Typography>
                                    {
                                        isAllowedEditChecklist && !isStageGateCompleted && (
                                            <IconButton onClick={() => {
                                                setEditStatus({ status: true, id: String(artefact.id) });
                                                setFormStatus(true);
                                            }} sx={{ color: 'primary.main' }}>
                                                <Edit fontSize="small" />
                                            </IconButton>
                                        )
                                    }
                                </Box>
                                <Typography variant="caption">{artefact.description}</Typography>
                                <Divider sx={{ my: 1 }} />
                                <Box display={'flex'} alignItems={'center'} flexWrap={'wrap'} gap={1}>
                                    <Typography variant="caption">Category : {artefact.category}</Typography>
                                    <Typography className="caption">|</Typography>
                                    <Typography variant="caption">Owner : {artefact.assigned_to}</Typography>
                                    <Typography className="caption">|</Typography>
                                    <Typography variant="caption">Created at : {formatDate(artefact.created_at)}</Typography>
                                </Box>
                            </Box>
                        ))
                }
                {isPending && page > 1 && (
                    <Box display="flex" justifyContent="center" p={2}>
                        <CircularProgress size={24} />
                    </Box>
                )}
            </Box>

            <DrawerControl
                title={editStatus.status ? "Edit Artefact Requirement" : "Create Artefact Requirement"}
                subTitle={""}
                isOpen={formStatus}
                handleClose={() => setFormStatus(false)}
                children={
                    <CreateArtefacts
                        projectId={String(projectId)}
                        stageGateId={stageGateId}
                        isEdit={editStatus.status}
                        artefactId={editStatus.id}
                        onClose={() => setFormStatus(false)}
                        onSave={() => {
                            setEditStatus({ status: false, id: null });
                            setFormStatus(false);
                            setPage(1);
                            setAccumulatedData([]);
                            refetchArtefacts();
                        }}
                    />
                }
            />

        </Box>
    )
}